import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Container } from '@mui/material';
import { List as ListIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';

import { DepartmentSelectionSection } from '../components/department-selection-section';
import { ReportHeader } from '../components/report-header';
import { ReportNavigationPanel, ReportNavigationItem } from '../components/report-navigation-panel';
import { ReportSectionCard } from '../components/report-section-card';
import { exportReportsAchievement, exportReportsSchedule } from '../services';
import { PropertyOption, ReportFormData } from '../types/report.types';
import {
  buildDeliveryStaffOptions,
  isStaffInSelectedDepartments,
} from '../utils/build-delivery-staff-options';
import { handleOutputReportsExportError } from '../utils/handle-output-reports-export-error';

import { useDepartments } from '@/features/companies/hooks/use-departments';
import { staffsService } from '@/features/companies/services/staffs.service';
import { Staff } from '@/features/companies/types';
import { propertiesService } from '@/features/properties/services/properties.service';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { FunctionName } from '@/lib/enums';

const navigationItems: ReportNavigationItem[] = [
  { id: 'performance-checklist', label: '実績確認チェックリスト', icon: <ListIcon /> },
  { id: 'schedule-table', label: '予定表', icon: <CalendarIcon /> },
];

export const ReportsPage = () => {
  // Fetch departments from API
  const { departments } = useDepartments();
  const { setIsLoading } = useLoadingProgress();
  const { setConfirmDialog } = useConfirmDialog();

  // State to store all delivery staffs (loaded once)
  const [allDeliveryStaffs, setAllDeliveryStaffs] = useState<Staff[]>([]);

  // State to store property options (loaded once)
  const [propertyOptions, setPropertyOptions] = useState<PropertyOption[]>([]);
  const [propertyOptionsLoading, setPropertyOptionsLoading] = useState(false);

  const [formData, setFormData] = useState<ReportFormData>({
    performanceChecklist: {
      placementDateFrom: '',
      placementDateTo: '',
      departmentCodes: [],
    },
    scheduleTable: {
      staffIds: [],
      propertyIds: [],
      placementDateFrom: '',
      placementDateTo: '',
      inputRows: 0,
      departmentCodes: [],
    },
    deliveryConfirmation: {
      staffIds: [],
      propertyIds: [],
      placementDateFrom: '',
      placementDateTo: '',
      inputRows: 0,
    },
  });

  const [activeSection, setActiveSection] = useState<string>('performance-checklist');

  const handlePerformanceChecklistOutput = useCallback(async () => {
    setIsLoading(true);
    try {
      await exportReportsAchievement({
        ...formData.performanceChecklist,
      });
      setIsLoading(false);
    } catch (error: unknown) {
      console.error('Error exporting achievement:', error);
      handleOutputReportsExportError(error, setConfirmDialog);
      setIsLoading(false);
    }
  }, [formData.performanceChecklist, setIsLoading, setConfirmDialog]);

  const handleScheduleTableOutput = useCallback(async () => {
    setIsLoading(true);
    try {
      const propertyIds = Array.from(
        new Set(formData.scheduleTable.propertyIds.filter((id) => id !== ''))
      );
      await exportReportsSchedule({
        ...formData.scheduleTable,
        propertyIds,
      });
      setIsLoading(false);
    } catch (error: unknown) {
      console.error('Error exporting schedule table:', error);
      handleOutputReportsExportError(error, setConfirmDialog);
      setIsLoading(false);
    }
  }, [formData.scheduleTable, setIsLoading, setConfirmDialog]);

  const handleAddInputRow = useCallback((section: 'scheduleTable' | 'deliveryConfirmation') => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        inputRows: Math.min(prev[section].inputRows + 1, 10),
      },
    }));
  }, []);

  const handleDepartmentCodesChange = useCallback((codes: number[]) => {
    setFormData((prev) => ({
      ...prev,
      performanceChecklist: {
        ...prev.performanceChecklist,
        departmentCodes: codes,
      },
    }));
  }, []);

  const handleScheduleTableDepartmentCodesChange = useCallback(
    (codes: number[]) => {
      setFormData((prev) => {
        const prevStaffIds = prev.scheduleTable.staffIds;
        const keptStaffIds =
          codes.length === 0
            ? []
            : prevStaffIds.filter((id) => {
                const staff = allDeliveryStaffs.find((s) => s.id === id);
                return staff ? isStaffInSelectedDepartments(staff, codes) : false;
              });

        return {
          ...prev,
          scheduleTable: {
            ...prev.scheduleTable,
            departmentCodes: codes,
            staffIds: keptStaffIds,
          },
        };
      });
    },
    [allDeliveryStaffs]
  );

  // Load all delivery staffs and property options once when component mounts
  useEffect(() => {
    let isMounted = true;

    const loadDeliveryStaffs = async () => {
      if (allDeliveryStaffs.length > 0 || !isMounted) return;

      try {
        const [deliveryStaffs, outsourcingStaffs] = await Promise.all([
          staffsService.getStaffs({
            role_key: 'delivery',
            function_name: FunctionName.COMPANY_MANAGEMENT,
          }),
          staffsService.getStaffs({
            role_key: 'outsourcing_delivery',
            function_name: FunctionName.COMPANY_MANAGEMENT,
          }),
        ]);

        if (!isMounted) return;

        const combinedStaffs = [...deliveryStaffs, ...outsourcingStaffs];
        const uniqueStaffs = Array.from(
          new Map(combinedStaffs.map((staff) => [staff.id, staff])).values()
        );

        setAllDeliveryStaffs(uniqueStaffs);
      } catch (error) {
        console.error('Error loading all delivery staffs:', error);
      }
    };

    const loadPropertyOptions = async () => {
      if (propertyOptions.length > 0 || !isMounted) return;

      setPropertyOptionsLoading(true);
      try {
        const properties = await propertiesService.getPropertyNames({
          hasArchived: false,
          functionName: FunctionName.PROPERTY_MANAGEMENT,
        });

        if (!isMounted) return;

        const options: PropertyOption[] = properties.map((property) => ({
          key: property.id,
          value: property.name,
          label: property.name,
        }));

        setPropertyOptions([{ key: '', value: '', label: '指定なし' }, ...options]);
      } catch (error) {
        console.error('Error loading property options:', error);
        if (isMounted) {
          setPropertyOptions([{ key: '', value: '', label: '指定なし' }]);
        }
      } finally {
        if (isMounted) {
          setPropertyOptionsLoading(false);
        }
      }
    };

    const loadInitialData = async () => {
      await Promise.all([loadDeliveryStaffs(), loadPropertyOptions()]);
    };

    void loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [allDeliveryStaffs.length, propertyOptions.length]);

  const deliveryStaffOptions = useMemo(
    () => buildDeliveryStaffOptions(allDeliveryStaffs, formData.scheduleTable.departmentCodes),
    [formData.scheduleTable.departmentCodes, allDeliveryStaffs]
  );

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <ReportHeader title='帳票出力' subtitle='条件を指定して帳票を出力できます。' />

      {/* Two Column Layout */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left Column - Report Sections */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {/* Performance Confirmation Checklist */}
          <Box id='performance-checklist' sx={{ mb: 3 }}>
            <ReportSectionCard
              id='performance-checklist-card'
              title='実績確認チェックリスト'
              startDate={formData.performanceChecklist.placementDateFrom}
              endDate={formData.performanceChecklist.placementDateTo}
              onStartDateChange={(date) =>
                setFormData((prev) => ({
                  ...prev,
                  performanceChecklist: { ...prev.performanceChecklist, placementDateFrom: date },
                }))
              }
              onEndDateChange={(date) =>
                setFormData((prev) => ({
                  ...prev,
                  performanceChecklist: { ...prev.performanceChecklist, placementDateTo: date },
                }))
              }
              onOutput={handlePerformanceChecklistOutput}
              outputButtonText='帳票出力'
            >
              {/* Department Selection Section - Reuse from members */}
              <DepartmentSelectionSection
                departments={departments}
                selectedDepartmentCodes={formData.performanceChecklist.departmentCodes}
                onDepartmentCodesChange={handleDepartmentCodesChange}
              />
            </ReportSectionCard>
          </Box>

          {/* Schedule Table */}
          <ReportSectionCard
            id='schedule-table'
            title='予定表'
            startDate={formData.scheduleTable.placementDateFrom}
            endDate={formData.scheduleTable.placementDateTo}
            onStartDateChange={(date) =>
              setFormData((prev) => ({
                ...prev,
                scheduleTable: { ...prev.scheduleTable, placementDateFrom: date },
              }))
            }
            onEndDateChange={(date) =>
              setFormData((prev) => ({
                ...prev,
                scheduleTable: { ...prev.scheduleTable, placementDateTo: date },
              }))
            }
            onOutput={handleScheduleTableOutput}
            deliveryStaffOptions={deliveryStaffOptions}
            selectedDeliveryStaff={formData.scheduleTable.staffIds}
            onDeliveryStaffChange={(selectedIds) =>
              setFormData((prev) => ({
                ...prev,
                scheduleTable: { ...prev.scheduleTable, staffIds: selectedIds },
              }))
            }
            desiredNames={formData.scheduleTable.propertyIds || []}
            onDesiredNamesChange={(names) =>
              setFormData((prev) => ({
                ...prev,
                scheduleTable: { ...prev.scheduleTable, propertyIds: names },
              }))
            }
            propertyOptions={propertyOptions}
            propertyOptionsLoading={propertyOptionsLoading}
            showDragDrop={true}
            onAddInputRow={() => handleAddInputRow('scheduleTable')}
            inputRowCount={formData.scheduleTable.inputRows}
            maxInputRows={10}
            selectedDepartmentCodes={formData.scheduleTable.departmentCodes}
          >
            {/* Department Selection Section - Reuse from members */}
            <DepartmentSelectionSection
              departments={departments}
              selectedDepartmentCodes={formData.scheduleTable.departmentCodes}
              onDepartmentCodesChange={handleScheduleTableDepartmentCodesChange}
            />
          </ReportSectionCard>
        </Box>

        {/* Right Column - Navigation Panel */}
        <Box sx={{ width: 250, flexShrink: 0 }}>
          <ReportNavigationPanel
            items={navigationItems}
            activeSection={activeSection}
            onSectionClick={setActiveSection}
          />
        </Box>
      </Box>
    </Container>
  );
};
